import React, { useEffect, useState } from "react";
import { ourTeamApi, getImageUrl } from "../api";
import ImageLoader from "./common/ImageLoader";
import "./AdminOurTeam.css";

// --- Custom PIN Confirmation Modal (Preserving 8238 logic) ---
const ConfirmationModal = ({ item, onConfirm, onCancel }) => {
  const [step, setStep] = useState(1);
  const [inputCode, setInputCode] = useState("");
  const REQUIRED_CODE = "8238";

  const isYear = item.type === "year";
  const label = item.name;
  const action = isYear
    ? "delete the entire Year & all its Members"
    : "delete this Member";

  const handleInitialConfirm = () => {
    setStep(2);
  };

  const handleFinalConfirm = () => {
    if (inputCode === REQUIRED_CODE) {
      onConfirm(item.id, isYear);
      onCancel();
    } else {
      alert("❌ Incorrect code. Deletion canceled.");
      setInputCode("");
    }
  };

  return (
    <div className="confirmation-overlay">
      <div className="confirmation-modal">
        <div className="modal-warning-icon">
          <i className="fas fa-exclamation-triangle"></i>
        </div>
        <h3>Action Confirmation Required</h3>
        {step === 1 && (
          <>
            <p className="modal-text">
              Are you sure you want to {action}: <strong>{label}</strong>?
            </p>
            <div className="modal-buttons">
              <button className="btn-cancel" onClick={onCancel}>
                No, Keep It
              </button>
              <button
                className="btn-confirm-step1"
                onClick={handleInitialConfirm}
              >
                Yes, Proceed
              </button>
            </div>
          </>
        )}
        {step === 2 && (
          <>
            <p className="modal-text">
              Please enter the security PIN <strong>{REQUIRED_CODE}</strong>{" "}
              below to confirm deletion of: <strong>{label}</strong>.
            </p>
            <input
              type="text"
              placeholder="Enter 8238"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value)}
              maxLength="4"
              className="pin-modal-input"
              autoFocus
            />
            <div className="modal-buttons">
              <button className="btn-cancel" onClick={onCancel}>
                Cancel
              </button>
              <button
                className="btn-confirm-step2"
                onClick={handleFinalConfirm}
                disabled={inputCode.length !== 4}
              >
                Confirm Delete
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

const AdminOurTeam = () => {
  const [years, setYears] = useState([]);
  const [newYearLabel, setNewYearLabel] = useState("");
  const [selectedYearId, setSelectedYearId] = useState("");
  const [name, setName] = useState("");
  const [title, setTitle] = useState("");
  const [memberRole, setMemberRole] = useState("executive");
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const res = await ourTeamApi.getAll();
      setYears(res.data || []);
      if (res.data && res.data.length > 0 && !selectedYearId) {
        setSelectedYearId(res.data[0].id);
      }
    } catch (err) {
      console.error("Fetch team error:", err);
    }
  };

  const handleAddYear = async (e) => {
    e.preventDefault();
    if (!newYearLabel.trim()) return;
    try {
      await ourTeamApi.createYear(newYearLabel.trim());
      setNewYearLabel("");
      fetchData();
      alert("✅ Team tenure year added successfully!");
    } catch (err) {
      console.error("Add year failed:", err);
      alert("❌ Failed to add year.");
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImage(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedYearId || !name || !title || !memberRole || !image) {
      return alert("Please select a Year and fill in Name, Role/Title, and Image.");
    }

    setLoading(true);
    const formData = new FormData();
    formData.append("name", name.trim().toUpperCase());
    formData.append("title", title.trim().toUpperCase());
    formData.append("memberRole", memberRole); // 'coordinator' or 'executive'
    formData.append("yearId", selectedYearId);
    formData.append("image", image);

    try {
      await ourTeamApi.createMember(formData);
      setName("");
      setTitle("");
      setImage(null);
      setPreviewUrl(null);
      e.target.reset();
      fetchData();
      alert("✅ Team member added successfully!");
    } catch (err) {
      console.error("Add member failed:", err);
      alert("❌ Failed to add member.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteYearClick = (year) => {
    setDeleteConfirm({ id: year.id, name: year.label, type: "year" });
  };

  const handleDeleteMemberClick = (member) => {
    setDeleteConfirm({ id: member.id, name: member.name, type: "member" });
  };

  const handleFinalDelete = async (id, isYear) => {
    try {
      if (isYear) {
        await ourTeamApi.deleteYear(id);
        alert("🗑️ Year and members deleted!");
      } else {
        await ourTeamApi.deleteMember(id);
        alert("🗑️ Member deleted!");
      }
      fetchData();
    } catch (err) {
      console.error("Delete failed:", err);
      alert("❌ Deletion failed.");
    }
    setDeleteConfirm(null);
  };

  return (
    <div className="admin-ourteam-wrapper">
      {deleteConfirm && (
        <ConfirmationModal
          item={deleteConfirm}
          onConfirm={handleFinalDelete}
          onCancel={() => setDeleteConfirm(null)}
        />
      )}

      <div className="admin-panel-header">
        <div className="panel-title-group">
          <i className="fas fa-users-cog title-icon"></i>
          <div>
            <h2>Manage Chapter Team Directory</h2>
            <p className="panel-subtitle">
              Manage Institute Coordinators and Executive Committee student leaders across academic years
            </p>
          </div>
        </div>
      </div>

      {/* Year Bar */}
      <div className="year-management-bar">
        <span className="bar-label">
          <i className="fas fa-user-plus"></i> Add Team Tenure Year
        </span>
        <form onSubmit={handleAddYear} className="year-form-inline">
          <input
            type="text"
            placeholder="e.g. 2025-2026"
            value={newYearLabel}
            onChange={(e) => setNewYearLabel(e.target.value)}
            required
          />
          <button type="submit" className="add-year-btn">
            + Create Year
          </button>
        </form>
      </div>

      {/* Member Form */}
      <form onSubmit={handleAddMember} className="event-form-card">
        <h3 className="sub-heading-tag">
          <i className="fas fa-id-card"></i> Add Member to Directory
        </h3>

        <div className="event-form-grid">
          <div className="admin-form-group">
            <label>Select Team Year *</label>
            <select
              value={selectedYearId}
              onChange={(e) => setSelectedYearId(e.target.value)}
              required
            >
              <option value="">Select Tenure Year *</option>
              {years.map((y) => (
                <option key={y.id} value={y.id}>
                  {y.label}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-form-group">
            <label>Member Category *</label>
            <select
              value={memberRole}
              onChange={(e) => setMemberRole(e.target.value)}
              required
            >
              <option value="executive">Executive Committee</option>
              <option value="coordinator">Institute Coordinator / Mentor</option>
            </select>
          </div>

          <div className="admin-form-group">
            <label>Full Name *</label>
            <input
              type="text"
              placeholder="e.g. JOHN DOE"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="admin-form-group">
            <label>Designation / Role Title *</label>
            <input
              type="text"
              placeholder="e.g. ACM CHAIR / FACULTY SPONSOR"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
            />
          </div>

          <div className="admin-form-group span-2">
            <label>
              <i className="fas fa-camera"></i> Member Avatar Photo *
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              required
            />
            {previewUrl && (
              <div className="avatar-preview-box">
                <img src={previewUrl} alt="Preview" />
              </div>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="admin-btn-primary submit-event-btn"
          disabled={loading}
        >
          {loading ? (
            <>
              <i className="fas fa-circle-notch fa-spin"></i> Adding...
            </>
          ) : (
            <>
              <i className="fas fa-user-check"></i> Save &amp; Add Member
            </>
          )}
        </button>
      </form>

      {/* Directory Display */}
      <div className="team-directory-display">
        {years.map((y) => {
          // Coordinators and Executives
          const coordinators =
            y.InstituteCoordinators ||
            y.OurTeamMembers?.filter((m) => m.memberRole === "coordinator") ||
            [];
          const executives =
            y.ExecutiveCommitteeMembers ||
            y.OurTeamMembers?.filter((m) => m.memberRole === "executive") ||
            [];

          return (
            <div key={y.id} className="year-timeline-card">
              <div className="year-card-header">
                <div className="year-title-pill">
                  <i className="fas fa-users"></i>
                  <span>{y.label} Chapter Team</span>
                </div>
                <button
                  className="delete-year-action-btn"
                  onClick={() => handleDeleteYearClick(y)}
                >
                  <i className="fas fa-trash-alt"></i> Delete Year &amp; Members
                </button>
              </div>

              {/* Coordinators */}
              {coordinators.length > 0 && (
                <div className="category-group">
                  <h4 className="category-group-title">
                    <i className="fas fa-chalkboard-teacher"></i> Institute Coordinators ({coordinators.length})
                  </h4>
                  <div className="members-cards-grid">
                    {coordinators.map((m) => (
                      <div key={m.id} className="member-profile-card">
                        <div className="member-avatar-box">
                          <ImageLoader
                            src={getImageUrl(m.imagePath)}
                            alt={m.name}
                          />
                        </div>
                        <div className="member-info-body">
                          <h4>{m.name}</h4>
                          <span className="member-role-tag">{m.title}</span>
                          <div className="member-card-footer">
                            <span className="member-id-tag">ID: #{m.id}</span>
                            <button
                              className="delete-member-icon-btn"
                              onClick={() => handleDeleteMemberClick(m)}
                              title="Delete coordinator"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Executives */}
              {executives.length > 0 && (
                <div className="category-group">
                  <h4 className="category-group-title">
                    <i className="fas fa-user-graduate"></i> Executive Committee ({executives.length})
                  </h4>
                  <div className="members-cards-grid">
                    {executives.map((m) => (
                      <div key={m.id} className="member-profile-card">
                        <div className="member-avatar-box">
                          <ImageLoader
                            src={getImageUrl(m.imagePath)}
                            alt={m.name}
                          />
                        </div>
                        <div className="member-info-body">
                          <h4>{m.name}</h4>
                          <span className="member-role-tag">{m.title}</span>
                          <div className="member-card-footer">
                            <span className="member-id-tag">ID: #{m.id}</span>
                            <button
                              className="delete-member-icon-btn"
                              onClick={() => handleDeleteMemberClick(m)}
                              title="Delete executive"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {coordinators.length === 0 && executives.length === 0 && (
                <p className="no-events-subtext">
                  No members added for {y.label} yet.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default AdminOurTeam;